import {
  BulkItemTemplate,
  CreateBulkItemTemplate,
  CreateDescriptionTemplate,
  DescriptionTemplate,
  GithubInstallation,
  GithubIntegrationService,
  SlackInstallation,
  SlackIntegrationService,
  StoredView,
  SubscriptionsService,
  UpdateBulkItemTemplate,
  UpdateDescriptionTemplate,
} from '@/api';
import { PreparedItemRequest } from '@/models/item-request.models';
import {
  OrganizationBase,
  OrganizationMembership,
  OrganizationMembershipStatus,
} from '@/models/user.models';
import dayjs from '@/utils/dayjs';
import { LocalStorageKeys } from '@/utils/local-storage';
import { defineStore } from 'pinia';
import { TabOrderItem } from '@/api';
import { SidebarFieldOrderItem } from '@/utils/sidebar-field-orders';
import { LocalStorage } from 'quasar';
import { apiRequestHandler } from '@/utils/api';
import { useAuthStore } from './authStore';
import {
  initOrganizationChannel,
  initTeamChannels,
  cleanupTeamChannels,
} from './events';
import { resetPrefetchCaches } from './helpers';
import { useItemStore } from './itemStore';
import {
  CreateSubscription,
  CreateProduct,
  Product,
  UpdateSubscription,
  UpdateProduct,
} from '@/api';
import { PaginatedQueryBase } from '@/models/common.models';
import {
  AdminUpdateOrganization,
  AssetsService,
  CreateCustomField,
  CreateCustomFieldCollection,
  CreateCustomFieldFromGroup,
  CreateEstimateOption,
  CreateEstimateOptionCollection,
  CreateImportJob,
  CreateImportJobRun,
  CreateItemRequest,
  CreateItemRequestGroup,
  CreateLabel,
  CreateLabelCollection,
  CreateLane,
  CreateLaneCollection,
  CreateOrganization,
  CreatePriority,
  CreatePriorityCollection,
  CreateUserLabelGroup,
  CreateWorkType,
  CustomFieldFormGroup,
  GenerateExportFile,
  ImporterService,
  ImportJobType,
  ItemRequest,
  ItemRequestGroup,
  Organization,
  OrganizationService,
  PreSignedUrlResponse,
  SubmitItemRequest,
  UpdateCustomField,
  UpdateCustomFieldCollection,
  UpdateCustomFieldFormGroup,
  UpdateEstimateOption,
  UpdateEstimateOptionCollection,
  UpdateItemRequest,
  UpdateItemRequestGroup,
  UpdateLabel,
  UpdateLabelCollection,
  UpdateLane,
  UpdateLaneCollection,
  UpdateOrganization,
  UpdatePriority,
  UpdatePriorityCollection,
  UpdateUserLabelGroup,
  UpdateWorkType,
} from '@/api';
import { AwsRum } from 'aws-rum-web';
import { prepareItemRequests } from '@/utils/item-requests';
import { UploadService } from '@/modules/upload.service';
import { useIntegrationsStore } from './integrationsStore';
import { isEqual } from 'lodash-es';
import { defaultTabOrder } from '@/utils/tab-orders';
import { isDefaultSidebarOrder } from '@/utils/sidebar-field-orders';

export interface OrganizationStateInterface {
  allOrganizations: Array<OrganizationBase>;
  activeOrganization: Organization | null;
  loadingOrganizations: boolean;
  githubInstallations: GithubInstallation[];
  githubInstallationLoading: boolean;
  slackInstallations: SlackInstallation[];
  slackInstallationLoading: boolean;
  storageLimitAlertTriggerIncrement: number;
  itemRequests: PreparedItemRequest[];
  itemRequestGroups: ItemRequestGroup[];
  allCustomFieldFormGroups: CustomFieldFormGroup[];
  temp_jiraAdoIntegrationsEnabled: boolean;
  descriptionTemplates: DescriptionTemplate[];
  bulkItemTemplates: BulkItemTemplate[];
}

export const useOrganizationStore = defineStore('organization', {
  /****
   STATE
   ****/
  state: (): OrganizationStateInterface => {
    return {
      allOrganizations: [],
      activeOrganization: null,
      githubInstallations: [],
      githubInstallationLoading: false,
      slackInstallations: [],
      slackInstallationLoading: false,
      loadingOrganizations: false,
      storageLimitAlertTriggerIncrement: 0,
      itemRequests: [],
      itemRequestGroups: [],
      allCustomFieldFormGroups: [],
      temp_jiraAdoIntegrationsEnabled: true,
      descriptionTemplates: [],
      bulkItemTemplates: [],
    };
  },

  /****
   GETTERS
   ****/
  getters: {
    workTypes(state) {
      return state.activeOrganization?.workTypes || [];
    },
    priorities(state) {
      return state.activeOrganization?.priorities;
    },
    labels(state) {
      return state.activeOrganization?.labels;
    },
    userLabelGroups(state) {
      return state.activeOrganization?.userLabelGroups || [];
    },
    trialSubscription(state) {
      return state.activeOrganization?.subscription?.status === 'trialing';
    },
    daysLeftInTrial(state) {
      const subscription = state.activeOrganization?.subscription;
      if (!subscription || !this.trialSubscription) return null;
      const today = dayjs(new Date());
      const futureDate = dayjs(subscription.trialEnd);
      const daysLeft = futureDate.diff(today, 'day');
      const roundDaysLeft = daysLeft + 1;
      if (roundDaysLeft <= 0) return 0;
      return roundDaysLeft;
    },
    paymentFailed(state) {
      const currentSubscriptionStatus =
        state.activeOrganization?.subscription?.status;
      if (!currentSubscriptionStatus) return false;
      return (
        currentSubscriptionStatus === 'past_due' ||
        currentSubscriptionStatus === 'unpaid' ||
        currentSubscriptionStatus === 'incomplete' ||
        currentSubscriptionStatus === 'incomplete_expired'
      );
    },
    paymentFailAccessRevoked(state) {
      const currentSubscriptionStatus =
        state.activeOrganization?.subscription?.status;
      if (!currentSubscriptionStatus) return false;
      return (
        currentSubscriptionStatus === 'unpaid' ||
        currentSubscriptionStatus === 'incomplete' ||
        currentSubscriptionStatus === 'incomplete_expired'
      );
    },
    subscriptionCancelled(state) {
      const currentSubscriptionStatus =
        state.activeOrganization?.subscription?.status;
      if (!currentSubscriptionStatus) return false;
      return currentSubscriptionStatus === 'canceled';
    },
  },

  /****
   ACTIONS
   ****/
  actions: {
    setActiveOrganization(organization: Organization) {
      this.activeOrganization = {
        ...organization,
      };
      LocalStorage.set(LocalStorageKeys.CURRENT_ORGANIZATION, organization.id);
      LocalStorage.set(
        LocalStorageKeys.LAST_VIEWED_ORGANIZATION_ID,
        organization.id
      );

      // Clean up existing team channels when switching organizations
      cleanupTeamChannels();

      // Reset prefetch caches to ensure fresh data for new organization
      resetPrefetchCaches();
    },

    async updateWorkTypeTabs(workTypeId: string, tabItems: TabOrderItem[]) {
      if (!this.activeOrganization || !workTypeId || !tabItems?.length) return;

      const workTypeIndex = this.activeOrganization.workTypes?.findIndex(
        (wt) => wt.id === workTypeId
      );
      if (workTypeIndex === undefined || workTypeIndex === -1) return;

      // Validate at least one tab is visible
      const visibleTabs = tabItems.filter((tab) => !tab.hidden);
      if (visibleTabs.length === 0) {
        throw new Error('At least one tab must remain visible');
      }

      const tabOrders = isEqual(tabItems, defaultTabOrder) ? [] : tabItems;

      try {
        const response = await OrganizationService.updateWorkType({
          path: {
            organizationId: this.activeOrganization.id,
            workTypeId,
          },
          body: {
            tabOrders,
          },
        });

        if (this.activeOrganization.workTypes) {
          this.activeOrganization.workTypes[workTypeIndex] = response.data.data;
        }
      } catch (error) {
        await this.refreshActiveOrganization();
        throw error;
      }
    },

    async updateWorkTypeSidebarFields(
      workTypeId: string,
      sidebarFieldItems: SidebarFieldOrderItem[]
    ) {
      if (!this.activeOrganization || !workTypeId || !sidebarFieldItems?.length)
        return;

      const workTypeIndex = this.activeOrganization.workTypes?.findIndex(
        (wt) => wt.id === workTypeId
      );
      if (workTypeIndex === undefined || workTypeIndex === -1) return;

      // Validate at least one field is visible
      const visibleFields = sidebarFieldItems.filter((field) => !field.hidden);
      if (visibleFields.length === 0) {
        throw new Error('At least one sidebar field must remain visible');
      }

      // If order matches default, send empty array to reset
      const sidebarFieldOrders = isDefaultSidebarOrder(sidebarFieldItems)
        ? []
        : sidebarFieldItems;

      try {
        const response = await OrganizationService.updateWorkType({
          path: {
            organizationId: this.activeOrganization.id,
            workTypeId,
          },
          body: {
            sidebarFieldOrders,
          },
        });

        if (this.activeOrganization.workTypes) {
          this.activeOrganization.workTypes[workTypeIndex] = response.data.data;
        }
      } catch (error) {
        await this.refreshActiveOrganization();
        throw error;
      }
    },

    removeOrganizationFromCache(orgId: string) {
      this.allOrganizations = this.allOrganizations.filter(
        (org) => org.id !== orgId
      );
    },

    async loadAll({ organizationName }: { organizationName: string }) {
      return apiRequestHandler(async () => {
        this.loadingOrganizations = true;
        await this.loadOrganizations(organizationName);
        this.loadingOrganizations = false;
      });
    },

    async loadOrganizations(organizationName?: string) {
      return apiRequestHandler(async () => {
        const authStore = useAuthStore();
        const organizations = (authStore.user?.organizationMemberships || [])
          .filter(
            (o: OrganizationMembership) =>
              o.status === OrganizationMembershipStatus.ACTIVE ||
              o.status === OrganizationMembershipStatus.IMPORTED
          )
          .map((o: OrganizationMembership) => o.organization);
        this.allOrganizations = organizations;
        let currentOrganizationId = LocalStorage.getItem<string>(
          LocalStorageKeys.CURRENT_ORGANIZATION
        );
        if (!currentOrganizationId) {
          currentOrganizationId = LocalStorage.getItem<string>(
            LocalStorageKeys.LAST_VIEWED_ORGANIZATION_ID
          );
        }
        if (organizationName) {
          const currentOrganization = organizations.find(
            (o) => o.name === organizationName
          );
          currentOrganizationId =
            currentOrganization?.id || currentOrganizationId;
        }
        if (!currentOrganizationId) {
          currentOrganizationId = organizations[0].id;
        } else {
          const currentOrganization = organizations.find(
            (o) => o.id === currentOrganizationId
          );
          if (!currentOrganization) {
            currentOrganizationId = organizations[0].id;
          }
        }
        await this.loadActiveOrganization(currentOrganizationId);
      });
    },

    async loadActiveOrganization(organizationId?: string) {
      if (!organizationId) {
        organizationId = this.activeOrganization?.id;
      }
      return apiRequestHandler(async () => {
        if (!organizationId) {
          throw 'No active organization selected';
        }
        const organization = await OrganizationService.getOrganizationById({
          path: { organizationId },
        });
        const authStore = useAuthStore();
        authStore.onPusherInitialized(() => {
          initOrganizationChannel(organization.data.data);

          // Initialize team channels for the current user's teams in this organization
          if (authStore.user && authStore.user.organizationMemberships) {
            const membership = authStore.user.organizationMemberships.find(
              (m) => m.organizationId === organization.data.data.id
            );
            if (membership && membership.teams) {
              initTeamChannels(organization.data.data.id, membership.teams);
            } else {
              initTeamChannels(organization.data.data.id, []);
            }
          }
        });
        this.setActiveOrganization(organization.data.data);

        this.allCustomFieldFormGroups =
          organization.data.data.customFieldFormGroups;
        const itemStore = useItemStore();
        // Load the root workstreams
        await itemStore.loadAllRootFolders();

        this.getGithubInstallation();
        this.getSlackInstallations();

        const integrationsStore = useIntegrationsStore();
        integrationsStore.loadApplicableMappings();

        // Check if AWS RUM is available
        const windowWithRum = window as Window & { awsRum?: AwsRum };
        if (windowWithRum.awsRum) {
          windowWithRum.awsRum.addSessionAttributes({
            organization_id: organization.data.data.id,
            organization_label: organization.data.data.label,
          });
        }
      });
    },

    async refreshActiveOrganization() {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      const organization = await OrganizationService.getOrganizationById({
        path: { organizationId },
      });
      this.setActiveOrganization(organization.data.data);
    },

    async organizationExists(name: string) {
      return apiRequestHandler(async () => {
        const response = await OrganizationService.checkIfOrganizationExists({
          query: { name },
        });
        return response.data;
      });
    },

    async getOrganizationsByDomain(domain: string) {
      return apiRequestHandler(async () => {
        return OrganizationService.findOrganizationsByDomain({
          path: { domain },
        });
      });
    },

    async createOrganization(organizationReq: CreateOrganization) {
      const response = await OrganizationService.createOrganization({
        body: organizationReq,
      });
      return response.data.data;
    },

    async updateOrganization(organizationReq: UpdateOrganization) {
      let organization = this.activeOrganization;
      if (!organization) return;
      const response = await OrganizationService.updateOrganization({
        path: { organizationId: organization.id },
        body: organizationReq,
      });
      organization = response.data.data;
      this.allOrganizations = this.allOrganizations.map((org) => {
        if (!organization) return org;
        if (org.id === organization.id) {
          return {
            id: organization.id,
            label: organization.label,
            name: organization.name,
            avatarUrl: organization.avatarUrl,
            industries: organization.industries,
            methodologies: organization.methodologies,
            size: organization.size,
          };
        }
        return org;
      });
      if (organization && this.activeOrganization?.id === organization.id) {
        this.activeOrganization = {
          ...this.activeOrganization,
          label: organization.label,
          name: organization.name,
          avatarUrl: organization.avatarUrl,
          domain: organization.domain,
          industries: organization.industries,
          methodologies: organization.methodologies,
          size: organization.size,
        };
      }
    },

    async uploadFile(file: File) {
      const uploadService = new UploadService();
      let urlResponse: PreSignedUrlResponse;
      try {
        const response = await AssetsService.getOrganizationUploadUrl({
          path: {
            organizationId: this.activeOrganization!.id,
          },
          query: {
            fileName: file.name,
            fileType: file.type,
          },
        });
        urlResponse = response.data;
      } catch (e) {
        const error = e as { response?: { data: { code: string } } };
        if (error.response?.data.code === 'SUBSCRIPTION.LIMIT_REACHED') {
          this.storageLimitAlertTriggerIncrement++;
          return '';
        } else {
          throw e;
        }
      }
      return uploadService.uploadFile(
        urlResponse.url,
        urlResponse.fields,
        file
      );
    },

    async uploadImporterFile(file: File) {
      const uploadService = new UploadService();
      let urlResponse: PreSignedUrlResponse;
      try {
        const response = await ImporterService.getImportUploadUrl({
          path: {
            organizationId: this.activeOrganization!.id,
          },
          query: {
            fileName: file.name,
            fileType: file.type,
          },
        });
        urlResponse = response.data;
        return uploadService.uploadFile(
          urlResponse.url,
          urlResponse.fields,
          file,
          true
        );
      } catch (e) {
        const error = e as { response?: { data: { code: string } } };
        if (error.response?.data.code === 'SUBSCRIPTION.LIMIT_REACHED') {
          this.storageLimitAlertTriggerIncrement++;
          return '';
        } else {
          throw e;
        }
      }
    },

    async createImportTemplate(importDataUrl: string) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      const importJob: CreateImportJob = {
        type: ImportJobType.TEMPLATE,
        importDataUrl,
      };
      const response = await ImporterService.createImportJob({
        path: {
          organizationId,
        },
        body: importJob,
      });
      return response.data.data;
    },

    async createImportJobDryRun({
      jobId,
      data,
    }: {
      jobId: string;
      data: CreateImportJobRun;
    }) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return await apiRequestHandler(async () => {
        const response = await ImporterService.createImportJobRun({
          path: {
            organizationId,
            importJobId: jobId,
          },
          body: data,
        });
        return response.data;
      });
    },

    async uploadPublicItemRequestFile(
      organizationId: Organization['id'],
      itemRequestId: ItemRequest['id'],
      file: File
    ) {
      let response: PreSignedUrlResponse;
      try {
        const res = await OrganizationService.getPublicItemRequestUploadUrl({
          path: {
            organizationId,
            itemRequestId,
          },
          query: {
            fileName: file.name,
            fileType: file.type,
          },
        });
        response = res.data;
      } catch (e) {
        const error = e as { response?: { data: { code: string } } };
        if (error.response?.data.code === 'SUBSCRIPTION.LIMIT_REACHED') {
          this.storageLimitAlertTriggerIncrement++;
          return '';
        } else {
          throw e;
        }
      }
      const uploadService = new UploadService();
      return uploadService.uploadFile(response.url, response.fields, file);
    },

    async createUserLabelGroup({
      organizationId,
      group,
    }: {
      organizationId: string;
      group: CreateUserLabelGroup;
    }) {
      await OrganizationService.createUserLabelGroup({
        path: { organizationId },
        body: group,
      });
      return this.loadActiveOrganization(organizationId);
    },

    async updateUserLabelGroup({
      userLabelGroupId,
      group,
    }: {
      userLabelGroupId: string;
      group: UpdateUserLabelGroup;
    }) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      await OrganizationService.updateUserLabelGroup({
        path: { organizationId, userLabelGroupId },
        body: group,
      });
      return this.loadActiveOrganization(organizationId);
    },

    async deleteUserLabelGroup({ groupId }: { groupId: string }) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      await OrganizationService.deleteUserLabelGroup({
        path: {
          organizationId,
          userLabelGroupId: groupId,
        },
      });
      return this.loadActiveOrganization(organizationId);
    },

    async getGithubInstallation() {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;

      try {
        this.githubInstallationLoading = true;
        const installation =
          await GithubIntegrationService.findGithubInstallationsByOrganizationId<true>(
            {
              path: { organizationId },
            }
          );
        this.githubInstallations = installation.data.data;
      } catch (e) {
        this.githubInstallations = [];
      } finally {
        this.githubInstallationLoading = false;
      }
    },

    async createGithubInstallation({
      installationId,
    }: {
      installationId: number;
    }) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;

      await GithubIntegrationService.createGithubInstallation({
        path: { organizationId },
        body: { installationId: installationId.toString() },
      });
      return this.getGithubInstallation();
    },

    async getSlackInstallations() {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      try {
        this.slackInstallationLoading = true;
        const response =
          await SlackIntegrationService.findSlackInstallationsByOrganizationId<true>(
            {
              path: { organizationId },
            }
          );
        this.slackInstallations = response.data?.data || [];
      } catch (e) {
        this.slackInstallations = [];
      } finally {
        this.slackInstallationLoading = false;
      }
    },

    async removeSlackInstallation(workspaceId: string) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      try {
        await SlackIntegrationService.deleteSlackInstallation<true>({
          path: {
            organizationId,
            workspaceId,
          },
        });
        await this.getSlackInstallations();
      } catch (e) {
        console.error('Failed to remove Slack installation:', e);
        throw e;
      }
    },

    async initiateSlackOAuth() {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      try {
        const response = await SlackIntegrationService.initiateSlackOAuth<true>(
          {
            path: {
              organizationId,
            },
          }
        );
        return response.data;
      } catch (e) {
        console.error('Failed to initiate Slack OAuth:', e);
        throw e;
      }
    },

    async createLabel(label: CreateLabel) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      const labelResponse = await OrganizationService.createLabel({
        path: { organizationId },
        body: label,
      });
      await this.loadActiveOrganization(organizationId);
      return labelResponse.data.data;
    },

    async updateLabel({
      labelId,
      label,
    }: {
      labelId: string;
      label: UpdateLabel;
    }) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      const labelResponse = await OrganizationService.updateLabel({
        path: {
          organizationId,
          labelId,
        },
        body: label,
      });
      await this.loadActiveOrganization(organizationId);
      return labelResponse;
    },

    async deleteLabel(labelId: string) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      await OrganizationService.deleteLabel({
        path: {
          organizationId,
          labelId,
        },
      });
      await this.loadActiveOrganization(organizationId);
    },

    async createLabelCollection(labelCollection: CreateLabelCollection) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      const labelCollectionResponse =
        await OrganizationService.createLabelCollection({
          path: { organizationId },
          body: labelCollection,
        });
      await this.loadActiveOrganization(organizationId);
      return labelCollectionResponse.data;
    },

    async updateLabelCollection({
      labelCollectionId,
      labelCollection,
      revertCallback,
    }: {
      labelCollectionId: string;
      labelCollection: UpdateLabelCollection;
      revertCallback?: () => void;
    }) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      try {
        const labelCollectionResponse =
          await OrganizationService.updateLabelCollection({
            path: {
              organizationId,
              labelCollectionId,
            },
            body: labelCollection,
          });
        await this.loadActiveOrganization(organizationId);
        return labelCollectionResponse.data.data;
      } catch (e) {
        if (revertCallback) {
          revertCallback();
        }
        throw e;
      }
    },

    async deleteLabelCollection(labelCollectionId: string) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      await OrganizationService.deleteLabelCollection({
        path: {
          organizationId,
          labelCollectionId,
        },
      });
      await this.loadActiveOrganization(organizationId);
    },

    async createPriority(priority: CreatePriority) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      const priorityResponse = await OrganizationService.createPriority({
        path: { organizationId },
        body: priority,
      });
      await this.loadActiveOrganization(organizationId);
      return priorityResponse.data.data;
    },

    async updatePriority({
      priorityId,
      priority,
    }: {
      priorityId: string;
      priority: UpdatePriority;
    }) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      const priorityResponse = await OrganizationService.updatePriority({
        path: {
          organizationId,
          priorityId,
        },
        body: priority,
      });
      await this.loadActiveOrganization(organizationId);
      return priorityResponse.data;
    },

    async deletePriority(priorityId: string) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      await OrganizationService.deletePriority({
        path: {
          organizationId,
          priorityId,
        },
      });
      await this.loadActiveOrganization(organizationId);
    },

    async createPriorityCollection(
      priorityCollection: CreatePriorityCollection
    ) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      const priorityCollectionResponse =
        await OrganizationService.createPriorityCollection({
          path: { organizationId },
          body: priorityCollection,
        });
      await this.loadActiveOrganization(organizationId);
      return priorityCollectionResponse.data;
    },

    async updatePriorityCollection({
      priorityCollectionId,
      priorityCollection,
      revertCallback,
    }: {
      priorityCollectionId: string;
      priorityCollection: UpdatePriorityCollection;
      revertCallback?: () => void;
    }) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      try {
        const priorityCollectionResponse =
          await OrganizationService.updatePriorityCollection({
            path: {
              organizationId,
              priorityCollectionId,
            },
            body: priorityCollection,
          });
        await this.loadActiveOrganization(organizationId);
        return priorityCollectionResponse.data.data;
      } catch (e) {
        if (revertCallback) {
          revertCallback();
        }
        throw e;
      }
    },

    async deletePriorityCollection(priorityCollectionId: string) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      await OrganizationService.deletePriorityCollection({
        path: {
          organizationId,
          priorityCollectionId,
        },
      });
      await this.loadActiveOrganization(organizationId);
    },
    async createLane(lane: CreateLane) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const newLane = await OrganizationService.createLane({
          path: { organizationId },
          body: lane,
        });
        await this.loadActiveOrganization(organizationId);
        return newLane.data.data;
      });
    },

    async updateLane({ laneId, data }: { laneId: string; data: UpdateLane }) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const updatedLane = await OrganizationService.updateLane({
          path: {
            organizationId,
            laneId,
          },
          body: data,
        });
        await this.loadActiveOrganization(organizationId);
        return updatedLane;
      });
    },

    async deleteLane(laneId: string) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        await OrganizationService.deleteLane({
          path: {
            organizationId,
            laneId,
          },
        });
        await this.loadActiveOrganization(organizationId);
      });
    },

    async createLaneCollection(laneCollection: CreateLaneCollection) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const newLaneCollection =
          await OrganizationService.createLaneCollection({
            path: {
              organizationId,
            },
            body: laneCollection,
          });
        await this.loadActiveOrganization(organizationId);
        return newLaneCollection;
      });
    },

    async updateLaneCollection({
      laneCollectionId,
      laneCollection,
      revertCallback,
    }: {
      laneCollectionId: string;
      laneCollection: UpdateLaneCollection;
      revertCallback?: () => void;
    }) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      try {
        const updatedLaneCollection =
          await OrganizationService.updateLaneCollection({
            path: {
              organizationId,
              laneCollectionId,
            },
            body: laneCollection,
          });
        await this.loadActiveOrganization(organizationId);
        return updatedLaneCollection;
      } catch (e) {
        if (revertCallback) {
          revertCallback();
        }
        throw e;
      }
    },

    async deleteLaneCollection(laneCollectionId: string) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      await OrganizationService.deleteLaneCollection({
        path: {
          organizationId,
          laneCollectionId,
        },
      });
      await this.loadActiveOrganization(organizationId);
    },

    async createEstimateOption(estimateOption: CreateEstimateOption) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const newEstimateOption =
          await OrganizationService.createEstimateOption({
            path: {
              organizationId,
            },
            body: estimateOption,
          });
        await this.loadActiveOrganization(organizationId);
        return newEstimateOption;
      });
    },

    async updateEstimateOption({
      estimateOptionId,
      data,
    }: {
      estimateOptionId: string;
      data: UpdateEstimateOption;
    }) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const updatedEstimateOption =
          await OrganizationService.updateEstimateOption({
            path: {
              organizationId,
              estimateOptionId,
            },
            body: data,
          });
        await this.loadActiveOrganization(organizationId);
        return updatedEstimateOption;
      });
    },

    async deleteEstimateOption(estimateOptionId: string) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      await OrganizationService.deleteEstimateOption({
        path: {
          organizationId,
          estimateOptionId,
        },
      });
      await this.loadActiveOrganization(organizationId);
    },

    async createEstimateOptionCollection(
      estimateOptionCollection: CreateEstimateOptionCollection
    ) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const newEstimateOptionCollection =
          await OrganizationService.createEstimateOptionCollection({
            path: {
              organizationId,
            },
            body: estimateOptionCollection,
          });
        await this.loadActiveOrganization(organizationId);
        return newEstimateOptionCollection;
      });
    },

    async updateEstimateOptionCollection({
      estimateOptionCollectionId,
      estimateOptionCollection,
      revertCallback,
    }: {
      estimateOptionCollectionId: string;
      estimateOptionCollection: UpdateEstimateOptionCollection;
      revertCallback?: () => void;
    }) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      try {
        const updatedEstimateOptionCollection =
          await OrganizationService.updateEstimateOptionCollection({
            path: {
              organizationId,
              estimateOptionCollectionId,
            },
            body: estimateOptionCollection,
          });
        await this.loadActiveOrganization(organizationId);
        return updatedEstimateOptionCollection;
      } catch (e) {
        if (revertCallback) {
          revertCallback();
        }
        throw e;
      }
    },

    async deleteEstimateOptionCollection(estimateOptionCollectionId: string) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      await OrganizationService.deleteEstimateOptionCollection({
        path: {
          organizationId,
          estimateOptionCollectionId,
        },
      });
      await this.loadActiveOrganization(organizationId);
    },

    async createCustomField(customField: CreateCustomField) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(
        async () => {
          const CustomField = await OrganizationService.createCustomField({
            path: { organizationId },
            body: customField,
          });
          await this.loadActiveOrganization(organizationId);
          return CustomField.data.data;
        },
        {
          errorCallback: (e) => {
            throw e;
          },
        }
      );
    },

    async updateCustomField({
      customFieldId,
      data,
    }: {
      customFieldId: string;
      data: UpdateCustomField;
    }) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const updatedCustomField = await OrganizationService.updateCustomField({
          path: {
            organizationId,
            customFieldId,
          },
          body: data,
        });
        await this.loadActiveOrganization(organizationId);
        return updatedCustomField;
      });
    },

    async deleteCustomField(customFieldId: string) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      await OrganizationService.deleteCustomField({
        path: {
          organizationId,
          customFieldId,
        },
      });
      await this.loadActiveOrganization(organizationId);
    },

    async createCustomFieldCollection(
      customFieldCollection: CreateCustomFieldCollection
    ) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const newCustomFieldCollection =
          await OrganizationService.createCustomFieldCollection({
            path: { organizationId },
            body: customFieldCollection,
          });
        await this.loadActiveOrganization(organizationId);
        return newCustomFieldCollection;
      });
    },

    async updateCustomFieldCollection({
      customFieldCollectionId,
      customFieldCollection,
      revertCallback,
    }: {
      customFieldCollectionId: string;
      customFieldCollection: UpdateCustomFieldCollection;
      revertCallback?: () => void;
    }) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      try {
        const updatedCustomFieldCollection =
          await OrganizationService.updateCustomFieldCollection({
            path: {
              organizationId,
              customFieldCollectionId,
            },
            body: customFieldCollection,
          });
        await this.loadActiveOrganization(organizationId);
        return updatedCustomFieldCollection;
      } catch (e) {
        if (revertCallback) {
          revertCallback();
        }
        throw e;
      }
    },

    async deleteCustomFieldCollection(customFieldCollectionId: string) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      await OrganizationService.deleteCustomFieldCollection({
        path: {
          organizationId,
          customFieldCollectionId,
        },
      });
      await this.loadActiveOrganization(organizationId);
    },

    async findCustomFieldFormGroups() {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await OrganizationService.findCustomFieldFormGroups({
          path: { organizationId },
        });
        this.allCustomFieldFormGroups = response.data.data;
        return response.data;
      });
    },

    async createCustomFieldFormGroup(data: CreateCustomFieldFromGroup) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await OrganizationService.createCustomFieldFormGroup({
          path: {
            organizationId,
          },
          body: data,
        });
        this.allCustomFieldFormGroups.push(response.data.data);
        return response;
      });
    },

    async updateCustomFieldFormGroup(
      data: UpdateCustomFieldFormGroup,
      customFieldFormGroupId: string
    ) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await OrganizationService.updateCustomFieldFormGroup({
          path: {
            organizationId,
            customFieldFormGroupId,
          },
          body: data,
        });
        await this.findCustomFieldFormGroups();

        return response;
      });
    },

    async deleteCustomFieldFormGroup(customFieldFormGroupId: string) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        await OrganizationService.deleteCustomFieldFormGroup({
          path: {
            organizationId,
            customFieldFormGroupId,
          },
        });
        this.allCustomFieldFormGroups = this.allCustomFieldFormGroups.filter(
          (item) => item.id !== customFieldFormGroupId
        );
      });
    },

    async loadProducts() {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        return (
          await SubscriptionsService.getProducts<true>({
            path: { organizationId },
          })
        ).data.data;
      });
    },
    async loadPaymentMethods() {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        return (
          await SubscriptionsService.getPaymentMethods<true>({
            path: { organizationId },
          })
        ).data.data;
      });
    },
    async checkIfPaymentExists() {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        return await SubscriptionsService.checkIfPaymentMethodsExists<true>({
          path: { organizationId },
        });
      });
    },
    async setupPaymentMethod() {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        return (
          await SubscriptionsService.setupPaymentMethod<true>({
            path: { organizationId },
          })
        ).data.data;
      });
    },
    async setDefaultPaymentMethod(paymentMethodId: string) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        return await SubscriptionsService.setDefaultPaymentMethod<true>({
          path: { organizationId },
          body: { paymentMethodId },
        });
      });
    },
    async deletePaymentMethod(paymentMethodId: string) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        return await SubscriptionsService.deletePaymentMethod<true>({
          path: { organizationId, paymentMethodId },
        });
      });
    },
    async updateSubscription(data: UpdateSubscription) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const result = await SubscriptionsService.updateSubscription<true>({
          path: { organizationId },
          body: data,
        });

        if (this.activeOrganization) {
          this.activeOrganization.subscription = {
            ...result.data.data,
            updatedAt: new Date().toISOString(),
          };
        }
        const authStore = useAuthStore();
        await authStore.refreshToken();
        return result;
      });
    },
    async createSubscription(data: CreateSubscription) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;

      const result = await SubscriptionsService.createSubscription<true>({
        path: { organizationId },
        body: data,
      });

      if (this.activeOrganization && result) {
        this.activeOrganization.subscription = {
          ...result.data.data,
          updatedAt: new Date().toISOString(),
        };
      }
      const authStore = useAuthStore();
      await authStore.refreshToken();
      return result;
    },
    async loadSubscriptionInvoices(query: PaginatedQueryBase) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        return (
          await SubscriptionsService.getSubscriptionInvoices<true>({
            path: { organizationId },
            query,
          })
        ).data;
      });
    },

    async getOrganizationCustomerData() {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        return (
          await SubscriptionsService.getCustomer<true>({
            path: { organizationId },
          })
        ).data;
      });
    },

    async getOrganizationCustomerDataById(organizationId: string) {
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        return (
          await SubscriptionsService.getCustomer<true>({
            path: { organizationId },
          })
        ).data;
      });
    },

    async getAllOrganizations() {
      const authStore = useAuthStore();
      if (!authStore.isDevStrideAdmin) {
        console.error(
          'Unauthorized - only admins that work at DevStride can query all organizations'
        );
        return;
      }
      const response = await OrganizationService.findOrganizations();
      return response.data.data;
    },
    async adminUpdateOrganization(
      organizationId: Organization['id'],
      data: AdminUpdateOrganization
    ) {
      const authStore = useAuthStore();
      if (!authStore.isDevStrideAdmin) {
        console.error(
          'Unauthorized - only admins that work at DevStride can update an organization'
        );
        return;
      }
      const response = await OrganizationService.adminUpdateOrganization({
        path: { organizationId },
        body: data,
      });
      return response.data.data;
    },

    async getAllSubscriptionsProducts() {
      const authStore = useAuthStore();
      if (!authStore.isDevStrideAdmin) {
        console.error(
          'Unauthorized - only admins that work at DevStride can query all subscriptions products'
        );
        return;
      }
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return (
        await SubscriptionsService.getProducts<true>({
          path: { organizationId },
        })
      ).data.data;
    },

    async createProduct(data: CreateProduct) {
      const authStore = useAuthStore();
      if (!authStore.isDevStrideAdmin) {
        console.error(
          'Unauthorized - only admins that work at DevStride can create a product'
        );
        return;
      }
      return (
        await SubscriptionsService.createProduct<true>({
          body: data,
        })
      ).data;
    },
    async updateProduct(
      subscriptionProductId: Product['id'],
      data: UpdateProduct
    ) {
      const authStore = useAuthStore();
      if (!authStore.isDevStrideAdmin) {
        console.error(
          'Unauthorized - only admins that work at DevStride can update a product'
        );
        return;
      }
      return (
        await SubscriptionsService.updateProduct<true>({
          path: { productId: subscriptionProductId },
          body: data,
        })
      ).data;
    },

    async createWorkType(workType: CreateWorkType) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      const workTypeResponse = await OrganizationService.addCreateWorkType({
        path: {
          organizationId,
        },
        body: workType,
      });
      await this.loadActiveOrganization(organizationId);
      return workTypeResponse.data.data;
    },

    async updateWorkType({
      workTypeId,
      workType,
    }: {
      workTypeId: string;
      workType: UpdateWorkType;
    }) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      const workTypeResponse = await OrganizationService.updateWorkType({
        path: {
          organizationId,
          workTypeId,
        },
        body: workType,
      });
      await this.loadActiveOrganization(organizationId);
      return workTypeResponse;
    },

    async deleteWorkType(workTypeId: string) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      await OrganizationService.archiveWorkType({
        path: {
          organizationId,
          workTypeId,
        },
      });
      await this.loadActiveOrganization(organizationId);
    },

    async findItemRequests() {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await OrganizationService.findItemRequests({
          path: { organizationId },
        });
        this.itemRequests = response.data.data
          ? prepareItemRequests(response.data.data)
          : [];

        return response;
      });
    },
    async findItemRequestGroups() {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await OrganizationService.findItemRequestGroups({
          path: { organizationId },
        });
        this.itemRequestGroups = response.data.data || [];
        return response;
      });
    },
    async createItemRequestGroup(data: CreateItemRequestGroup) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const itemRequestGroup =
          await OrganizationService.createItemRequestGroup({
            path: { organizationId },
            body: data,
          });
        this.itemRequestGroups.push(itemRequestGroup.data.data);
        return itemRequestGroup;
      });
    },
    async deleteItemRequestGroup(itemRequestGroupId: string) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const itemRequestGroup =
          await OrganizationService.deleteItemRequestGroup({
            path: {
              organizationId,
              itemRequestGroupId,
            },
          });
        if (itemRequestGroup) {
          this.itemRequestGroups = this.itemRequestGroups.filter(
            (irg) => irg.id !== itemRequestGroupId
          );
        }
        return itemRequestGroup;
      });
    },
    async updateItemRequestGroup(
      itemRequestGroupId: string,
      data: UpdateItemRequestGroup
    ) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const itemRequestGroup =
          await OrganizationService.updateItemRequestGroup({
            path: {
              organizationId,
              itemRequestGroupId,
            },
            body: data,
          });
        this.itemRequestGroups = this.itemRequestGroups.map((irg) =>
          irg.id === itemRequestGroupId ? itemRequestGroup.data.data : irg
        );
        return itemRequestGroup;
      });
    },
    async getPublicItemRequestById(
      organizationId: Organization['id'],
      itemRequestId: ItemRequest['id']
    ) {
      return apiRequestHandler(async () => {
        const response = await OrganizationService.getPublicItemRequestById({
          path: { organizationId, itemRequestId },
        });
        return response.data.data;
      });
    },

    async getPublicStoredViewById(
      organizationId: Organization['id'],
      storedViewId: StoredView['id']
    ) {
      return apiRequestHandler(async () => {
        const response = await OrganizationService.getPublicStoredViewById({
          path: { organizationId, storedViewId },
        });
        return response.data;
      });
    },
    async createItemRequest(data: CreateItemRequest) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const itemRequest = await OrganizationService.addItemRequest({
          path: { organizationId },
          body: data,
        });
        this.itemRequests.push(prepareItemRequests([itemRequest.data.data])[0]);

        return itemRequest.data.data;
      });
    },

    async updateItemRequest(itemRequestId: string, data: UpdateItemRequest) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const itemRequest = await OrganizationService.updateItemRequest({
          path: {
            organizationId,
            itemRequestId,
          },
          body: data,
        });
        this.itemRequests = this.itemRequests.map((ir) =>
          ir.id === itemRequestId
            ? prepareItemRequests([itemRequest.data.data])[0]
            : ir
        );

        return itemRequest.data.data;
      });
    },

    async deleteItemRequest(itemRequestId: string) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const itemRequest = await OrganizationService.deleteItemRequest({
          path: {
            organizationId,
            itemRequestId,
          },
        });

        if (itemRequest) {
          this.itemRequests = this.itemRequests.filter(
            (ir) => ir.id !== itemRequestId
          );
        }

        return itemRequest;
      });
    },

    async submitItemRequest(
      itemRequestId: ItemRequest['id'],
      data: SubmitItemRequest
    ) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await OrganizationService.submitItemRequest({
          path: {
            organizationId,
            itemRequestId,
          },
          body: data,
        });
        return response.data.data;
      });
    },

    async submitPublicItemRequest(
      organizationId: Organization['id'],
      itemRequestId: ItemRequest['id'],
      data: SubmitItemRequest
    ) {
      return apiRequestHandler(async () => {
        const res = await OrganizationService.submitPublicItemRequest({
          path: {
            organizationId,
            itemRequestId,
          },
          body: data,
        });
        return res.data;
      });
    },

    async generateExportFile(data: GenerateExportFile) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await ImporterService.generateExportFile({
          path: {
            organizationId,
          },
          body: data,
        });
        return response.data;
      });
    },

    async findDescriptionTemplates() {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response =
          await OrganizationService.findDescriptionTemplates<true>({
            path: { organizationId },
          });
        this.descriptionTemplates = response.data.data || [];
        return response.data;
      });
    },

    async createDescriptionTemplate(data: CreateDescriptionTemplate) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response =
          await OrganizationService.createDescriptionTemplate<true>({
            path: { organizationId },
            body: data,
          });

        this.descriptionTemplates.push(response.data.data);
        return response.data;
      });
    },

    async deleteDescriptionTemplate(templateId: DescriptionTemplate['id']) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response =
          await OrganizationService.deleteDescriptionTemplate<true>({
            path: { organizationId, descriptionTemplateId: templateId },
          });
        this.descriptionTemplates = this.descriptionTemplates.filter(
          (template) => template.id !== templateId
        );

        return response.data;
      });
    },

    async updateDescriptionTemplate(
      templateId: DescriptionTemplate['id'],
      data: UpdateDescriptionTemplate
    ) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response =
          await OrganizationService.updateDescriptionTemplate<true>({
            path: { organizationId, descriptionTemplateId: templateId },
            body: data,
          });

        this.descriptionTemplates = this.descriptionTemplates.map((template) =>
          template.id === templateId ? response.data.data : template
        );
        return response.data;
      });
    },

    async findBulkItemTemplates() {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await OrganizationService.findBulkItemTemplates<true>({
          path: { organizationId },
        });
        this.bulkItemTemplates = response.data.data || [];
        return response.data;
      });
    },

    async createBulkItemTemplate(data: CreateBulkItemTemplate) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await OrganizationService.createBulkItemTemplate<true>(
          {
            path: { organizationId },
            body: data,
          }
        );

        this.bulkItemTemplates.push(response.data.data);
        return response.data;
      });
    },

    async deleteBulkItemTemplate(templateId: string) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await OrganizationService.deleteBulkItemTemplate<true>(
          {
            path: { organizationId, bulkItemTemplateId: templateId },
          }
        );
        this.bulkItemTemplates = this.bulkItemTemplates.filter(
          (template) => template.id !== templateId
        );
        return response.data;
      });
    },

    async updateBulkItemTemplate(
      templateId: string,
      data: UpdateBulkItemTemplate
    ) {
      const organizationId = this.activeOrganization?.id;
      if (!organizationId) return;
      return apiRequestHandler(async () => {
        const response = await OrganizationService.updateBulkItemTemplate<true>(
          {
            path: { organizationId, bulkItemTemplateId: templateId },
            body: data,
          }
        );

        this.bulkItemTemplates = this.bulkItemTemplates.map((template) =>
          template.id === templateId ? response.data.data : template
        );
        return response.data;
      });
    },
  },
});
